'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  InlineDialog,
  InlineDialogHeader,
  InlineDialogBody,
  InlineDialogFooter,
  InlineDialogTitle,
} from './inline-dialog'
import {
  Timer,
  MessageSquare,
  Coffee,
  Bell,
  X,
  Plus,
  Edit3,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { toast } from 'sonner'
import { useTranslations } from 'next-intl'
import type { DemoTimer } from '../demo-types'

// Timer configuration
const TIMER_CONFIG: Record<string, { icon: typeof Timer; color: string; label: string }> = {
  countdown: { icon: Timer, color: 'text-emerald-400', label: 'Timer' },
  request_period: { icon: MessageSquare, color: 'text-violet-400', label: 'Request Period' },
  break_reminder: { icon: Coffee, color: 'text-amber-400', label: 'Break Reminder' },
  custom: { icon: Bell, color: 'text-cyan-400', label: 'Timer' },
}

// Timer row in panel
function TimerRow({ timer, currentTime, onCancel, onExtend, onEdit }: {
  timer: DemoTimer
  currentTime: number
  onCancel: () => void
  onExtend: () => void
  onEdit: () => void
}) {
  const config = TIMER_CONFIG[timer.type] || TIMER_CONFIG.countdown
  const Icon = config.icon
  const remaining = Math.max(0, Math.floor((timer.endsAt.getTime() - currentTime) / 1000))
  const hasEnded = remaining === 0
  const isLowTime = remaining <= 60 && !hasEnded

  const minutes = Math.floor(remaining / 60)
  const seconds = remaining % 60
  const timeDisplay = `${minutes}:${seconds.toString().padStart(2, '0')}`

  return (
    <div className={`flex items-center justify-between px-2 py-1 rounded border ${
      hasEnded ? 'border-red-500/50 bg-red-500/10' :
      isLowTime ? 'border-amber-500/30 bg-amber-500/5' :
      'border-zinc-800 bg-zinc-800/50'
    }`}>
      <div className="flex items-center gap-1.5 min-w-0 flex-1">
        <Icon className={`h-3.5 w-3.5 shrink-0 ${hasEnded ? 'text-red-400' : config.color}`} />
        <span className="text-xs text-white truncate">{timer.label || config.label}</span>
        {!timer.isPublic && <EyeOff className="h-2.5 w-2.5 text-zinc-500 shrink-0" />}
      </div>
      <div className="flex items-center gap-1.5">
        {hasEnded ? (
          <>
            <span className="text-xs font-medium text-red-400">Finished!</span>
            <Button size="sm" variant="ghost" onClick={onCancel} className="h-5 px-1.5 text-xs text-zinc-400 hover:text-white">
              Dismiss
            </Button>
          </>
        ) : (
          <>
            <span className={`font-mono text-xs font-bold tabular-nums ${isLowTime ? 'text-red-400 animate-pulse' : config.color}`}>
              {timeDisplay}
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button size="sm" variant="ghost" onClick={onExtend} className="h-5 w-5 p-0 text-zinc-400 hover:text-emerald-400">
                  <Plus className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-zinc-800 border-zinc-700 text-xs">+5 min</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button size="sm" variant="ghost" onClick={onEdit} className="h-5 w-5 p-0 text-zinc-400 hover:text-blue-400">
                  <Edit3 className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-zinc-800 border-zinc-700 text-xs">Edit</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button size="sm" variant="ghost" onClick={onCancel} className="h-5 w-5 p-0 text-zinc-400 hover:text-red-400">
                  <X className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-zinc-800 border-zinc-700 text-xs">Cancel</TooltipContent>
            </Tooltip>
          </>
        )}
      </div>
    </div>
  )
}

export interface TimersPanelProps {
  activeTimers: DemoTimer[]
  currentTime: number
  isTimerDialogOpen: boolean
  onSetTimerDialogOpen: (open: boolean) => void
  onStartTimer: (type: DemoTimer['type'], durationMinutes: number, label: string, isPublic: boolean) => void
  onCancelTimer: (timerId: string) => void
  onExtendTimer: (timerId: string, additionalMinutes: number) => void
  onEditTimer: (timerId: string, updates: { label?: string; isPublic?: boolean }) => void
}

export function TimersPanel({
  activeTimers,
  currentTime,
  isTimerDialogOpen,
  onSetTimerDialogOpen,
  onStartTimer,
  onCancelTimer,
  onExtendTimer,
  onEditTimer,
}: TimersPanelProps) {
  const t = useTranslations('demo')
  const [isTimerPanelExpanded, setIsTimerPanelExpanded] = useState(false)
  const [timerDuration, setTimerDuration] = useState('5')
  const [timerLabel, setTimerLabel] = useState('')
  const [timerIsPublic, setTimerIsPublic] = useState(true)
  const [timerType, setTimerType] = useState<DemoTimer['type']>('countdown')
  const [editingTimer, setEditingTimer] = useState<{ id: string; label: string; isPublic: boolean } | null>(null)

  const handleStartTimer = () => {
    onStartTimer(timerType, parseInt(timerDuration), timerLabel, timerIsPublic)
    onSetTimerDialogOpen(false)
    setTimerLabel('')
    setIsTimerPanelExpanded(true)
  }

  return (
    <>
      {/* Active Timers Panel */}
      {activeTimers.length > 0 && (
        <div className="space-y-1.5">
          <button
            onClick={() => setIsTimerPanelExpanded(!isTimerPanelExpanded)}
            className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10"
          >
            <div className="flex items-center gap-2">
              <Timer className="h-3.5 w-3.5 text-emerald-400" />
              <span className="text-xs font-medium text-emerald-400">
                {activeTimers.length > 1 ? t('activeTimersPlural', { count: activeTimers.length }) : t('activeTimers', { count: activeTimers.length })}
              </span>
            </div>
            {isTimerPanelExpanded ? <ChevronUp className="h-3.5 w-3.5 text-zinc-400" /> : <ChevronDown className="h-3.5 w-3.5 text-zinc-400" />}
          </button>
          {isTimerPanelExpanded && (
            <div className="space-y-1">
              {activeTimers.map(timer => (
                <TimerRow
                  key={timer.id}
                  timer={timer}
                  currentTime={currentTime}
                  onCancel={() => onCancelTimer(timer.id)}
                  onExtend={() => onExtendTimer(timer.id, 5)}
                  onEdit={() => setEditingTimer({ id: timer.id, label: timer.label, isPublic: timer.isPublic })}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Timer Dialog */}
      <InlineDialog open={isTimerDialogOpen} onOpenChange={onSetTimerDialogOpen} className="max-w-md">
        <InlineDialogHeader onClose={() => onSetTimerDialogOpen(false)}>
          <InlineDialogTitle className="flex items-center gap-2">
            <Timer className="h-5 w-5 text-emerald-400" />
            {t('startTimer')}
          </InlineDialogTitle>
        </InlineDialogHeader>
        <InlineDialogBody>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-zinc-300 mb-2">{t('timerType')}</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'countdown', label: t('general'), icon: Timer },
                  { value: 'request_period', label: t('requestPeriod'), icon: MessageSquare },
                  { value: 'break_reminder', label: t('breakReminder'), icon: Coffee },
                  { value: 'custom', label: t('custom'), icon: Bell },
                ].map(({ value, label, icon: Icon }) => (
                  <Button
                    key={value}
                    variant={timerType === value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTimerType(value as DemoTimer['type'])}
                    className={timerType === value ? 'bg-emerald-600 hover:bg-emerald-700' : 'border-zinc-700 hover:bg-zinc-800'}
                  >
                    <Icon className="h-3.5 w-3.5 mr-1.5" />{label}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-zinc-300 mb-2">{t('duration')}</p>
              <div className="flex flex-wrap gap-2">
                {['5', '10', '15', '20', '30', '45', '60'].map((mins) => (
                  <Button
                    key={mins}
                    variant={timerDuration === mins ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTimerDuration(mins)}
                    className={timerDuration === mins ? 'bg-emerald-600 hover:bg-emerald-700' : 'border-zinc-700 hover:bg-zinc-800'}
                  >
                    {mins}m
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-zinc-300 mb-2">{t('labelOptional')}</p>
              <Input
                placeholder={t('labelPlaceholder')}
                value={timerLabel}
                onChange={(e) => setTimerLabel(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg border border-zinc-800 bg-zinc-800/50">
              <div className="flex items-center gap-2">
                {timerIsPublic ? <Eye className="h-4 w-4 text-green-400" /> : <EyeOff className="h-4 w-4 text-zinc-400" />}
                <span className="text-sm font-medium text-white">{timerIsPublic ? t('visibleToAudience') : t('privateOnlyYou')}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setTimerIsPublic(!timerIsPublic)} className="text-zinc-400 hover:text-white">
                {timerIsPublic ? t('makePrivate') : t('makePublic')}
              </Button>
            </div>
          </div>
        </InlineDialogBody>
        <InlineDialogFooter>
          <Button variant="outline" onClick={() => onSetTimerDialogOpen(false)} className="border-zinc-700">{t('cancel')}</Button>
          <Button onClick={handleStartTimer} className="bg-gradient-to-r from-emerald-600 to-green-600">
            <Timer className="mr-2 h-4 w-4" />{t('startTimer')}
          </Button>
        </InlineDialogFooter>
      </InlineDialog>

      {/* Edit Timer Dialog */}
      <InlineDialog open={!!editingTimer} onOpenChange={(open) => !open && setEditingTimer(null)} className="max-w-md">
        <InlineDialogHeader onClose={() => setEditingTimer(null)}>
          <InlineDialogTitle className="flex items-center gap-2">
            <Edit3 className="h-5 w-5 text-blue-400" />
            {t('editTimer')}
          </InlineDialogTitle>
        </InlineDialogHeader>
        <InlineDialogBody>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-zinc-300 mb-2">{t('labelOptional')}</p>
              <Input
                placeholder={t('labelPlaceholder')}
                value={editingTimer?.label || ''}
                onChange={(e) => setEditingTimer(prev => prev ? { ...prev, label: e.target.value } : null)}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg border border-zinc-800 bg-zinc-800/50">
              <div className="flex items-center gap-2">
                {editingTimer?.isPublic ? <Eye className="h-4 w-4 text-green-400" /> : <EyeOff className="h-4 w-4 text-zinc-400" />}
                <span className="text-sm font-medium text-white">{editingTimer?.isPublic ? t('visibleToAudience') : t('privateOnlyYou')}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingTimer(prev => prev ? { ...prev, isPublic: !prev.isPublic } : null)}
                className="text-zinc-400 hover:text-white"
              >
                {editingTimer?.isPublic ? t('makePrivate') : t('makePublic')}
              </Button>
            </div>
          </div>
        </InlineDialogBody>
        <InlineDialogFooter>
          <Button variant="outline" onClick={() => setEditingTimer(null)} className="border-zinc-700">{t('cancel')}</Button>
          <Button
            onClick={() => {
              if (editingTimer) {
                onEditTimer(editingTimer.id, { label: editingTimer.label, isPublic: editingTimer.isPublic })
                toast.success(t('timerUpdated'))
                setEditingTimer(null)
              }
            }}
            className="bg-gradient-to-r from-blue-600 to-indigo-600"
          >
            <Edit3 className="mr-2 h-4 w-4" />{t('saveChanges')}
          </Button>
        </InlineDialogFooter>
      </InlineDialog>
    </>
  )
}
